import React, { useCallback, useContext, useRef, useState } from 'react';
import Tippy from 'utils/Tippy';

import AssignmentEventEmitter, { events } from 'modules/dispatching/assignment/events';
import {
  assignItineraryToAgent,
  breakItinerary,
  launchItinerary,
  moveItineraryToAuto,
  sendNoDriverWarningEmail,
  unassignItineraryFromAgent,
} from 'modules/dispatching/service';
import { useDispatchingUI } from 'modules/dispatching/ui';
import SetToManualFlowContext from 'modules/set-to-manual-flow/context';

import { events as RoutingEvents, RoutingEventEmitter } from 'modules/routing';

import { DISPATCHING } from 'modules/fleet/events';
import { useSelector } from 'react-redux';
import AssignAgent from './assign-agent';
import ReactTooltip from 'react-tooltip';
import { useHistory, useLocation } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { getClientConfig, constants } from 'redux/modules/client-config/selectors';

const TooltipContent = ({
  intl,
  id,
  journeyId,
  shipperOrderIds,
  route,
  isLocked,
  pickupDate,
  onFinish,
  isUpgrade = false,
  isAssignedToAgent,
  onUnMount,
}) => {
  const t = intl.messages.modules.dispatching.actions;
  const { setLoading, mutateListFn } = useDispatchingUI();
  const userId = useSelector((state) => state?.me?.id);
  const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);
  const { openModal, unlock, needsManualReasonsModal } = useContext(SetToManualFlowContext);
  const { pathname } = useLocation();
  const history = useHistory();
  const firstShipperOrderId = shipperOrderIds?.length && shipperOrderIds[0];

  const clientConfig = useSelector(getClientConfig);
  const assignOptions = useSelector((store) => getClientConfig(store, constants.ASSIGNATION));
  const assignDriver = clientConfig[constants.ASSIGN_DRIVER];
  const isAutomaticEnabled = clientConfig[constants.AUTOMATIC];
  const isRoutingEnabled = clientConfig[constants.ROUTING];
  const isEditOrderEnabled = clientConfig[constants.EDIT_ORDER_DISPATCHING];
  const isZendeskEnabled = clientConfig[constants.ZENDESK];

  const filteredAssignOptions = Object.keys(assignOptions).filter((key) => assignOptions[key]);
  const allowAssignDriver = assignDriver && filteredAssignOptions.length > 0;

  const handleAddToRouting = useCallback(
    (e) => {
      e.stopPropagation();

      DISPATCHING.ADD_TO_ROUTING({
        itinerary_id: id,
        journey_id: journeyId,
      });

      RoutingEventEmitter.emit(RoutingEvents.ADD_ITINERARY, {
        itineraryId: id,
        baseItineraryEndpoint: 'itineraries',
      });

      onFinish();
    },
    [id, journeyId, onFinish]
  );

  const handleAssign = useCallback(
    (e) => {
      e.stopPropagation();

      DISPATCHING.ASSIGN_TO_DRIVER_CLICK({
        itinerary_id: id,
        journey_id: journeyId,
      });

      AssignmentEventEmitter.emit(events.ASSIGN_ITINERARY, {
        date: pickupDate,
        itineraryId: id,
        itineraryRoute: route,
      });
      onFinish();
    },
    [id, onFinish, pickupDate, route, journeyId]
  );

  const beforeSubmit = useCallback(() => {
    setLoading((loading) => ({ ...loading, [id]: true }));
  }, [id, setLoading]);

  const afterSubmit = useCallback(async () => {
    setLoading((loading) => ({ ...loading, [id]: false }));
    mutateListFn();
  }, [id, setLoading, mutateListFn]);

  const handleMoveToManual = useCallback(
    async (e) => {
      DISPATCHING.MOVE_TO_MANUAL({
        itinerary_id: id,
        journey_id: journeyId,
      });
      const needsModal = await needsManualReasonsModal(id);
      if (!needsModal) {
        try {
          setLoading((loading) => ({ ...loading, [id]: true }));
          await unlock(id);
          await mutateListFn();
        } catch (err) {
          console.error(err);
        } finally {
          setLoading((loading) => ({ ...loading, [id]: false }));
        }
      } else {
        openModal(id, {
          beforeSubmit,
          afterSubmit,
        });
        onFinish();
      }
    },
    [
      id,
      openModal,
      onFinish,
      journeyId,
      mutateListFn,
      setLoading,
      unlock,
      beforeSubmit,
      afterSubmit,
      needsManualReasonsModal,
    ]
  );

  const handleMoveToAuto = useCallback(
    async (e) => {
      DISPATCHING.MOVE_TO_AUTO({
        itinerary_id: id,
        journey_id: journeyId,
      });

      try {
        setLoading((loading) => ({ ...loading, [id]: true }));
        await moveItineraryToAuto(id);
        await mutateListFn();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((loading) => ({ ...loading, [id]: false }));
      }
    },
    [id, journeyId, setLoading, mutateListFn]
  );

  const handleLaunchToThePool = useCallback(
    async (e) => {
      try {
        setLoading((loading) => ({ ...loading, [id]: true }));
        await launchItinerary(id);
        await mutateListFn();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((loading) => ({ ...loading, [id]: false }));
      }
    },
    [id, setLoading, mutateListFn]
  );

  const handleBreak = useCallback(
    async (e) => {
      try {
        setLoading((loading) => ({ ...loading, [id]: true }));
        await breakItinerary(id);
        await mutateListFn();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((loading) => ({ ...loading, [id]: false }));
      }
    },
    [id, setLoading, mutateListFn]
  );

  const handleShipperWarningEmail = useCallback(async () => {
    setLoading((loading) => ({ ...loading, [id]: true }));
    try {
      await sendNoDriverWarningEmail({ itineraryId: id });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((loading) => ({ ...loading, [id]: false }));
    }
  }, [id, setLoading]);

  const assignAgent = useCallback(
    async (agentId) => {
      try {
        setLoading((loading) => ({ ...loading, [id]: true }));
        await assignItineraryToAgent(id, agentId);
        await mutateListFn();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading((loading) => ({ ...loading, [id]: false }));
      }
    },
    [id, setLoading, mutateListFn]
  );

  const handleTakeThisOrder = useCallback(async () => {
    await assignAgent(userId);
  }, [userId, assignAgent]);

  const handleAssignAgent = useCallback(async () => {
    setShowAssignAgentModal(true);
    onFinish();
  }, [setShowAssignAgentModal, onFinish]);

  const handleUnassignAgent = useCallback(async () => {
    try {
      setLoading((loading) => ({ ...loading, [id]: true }));
      await unassignItineraryFromAgent(id);
      await mutateListFn();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading((loading) => ({ ...loading, [id]: false }));
    }
  }, [id, setLoading, mutateListFn]);

  const onAssignAgentModalClosed = useCallback(() => {
    setShowAssignAgentModal(false);
  }, [setShowAssignAgentModal]);

  const handleFinish = useCallback(() => {
    ReactTooltip.hide();
    onUnMount && onUnMount();
  }, [onUnMount]);

  const handleEditOrder = useCallback(() => {
    DISPATCHING.EDIT_ORDER_CLICK({
      shipper_order_id: firstShipperOrderId,
    });
    history.push(`/edit_shipper_order/${firstShipperOrderId}?redirect=${pathname}`);
    handleFinish();
  }, [handleFinish, firstShipperOrderId, history, pathname]);

  const notLocked = !isLocked;

  if (!id) {
    return null;
  }

  return (
    <>
      <div>
        {notLocked && (
          <>
            <button
              className="action_button"
              data-testid="actionButton"
              onClick={handleTakeThisOrder}
            >
              {t.take_this_order}
            </button>
            <button
              className="action_button"
              data-testid="actionButton"
              onClick={handleAssignAgent}
            >
              {t.assign_agent}
            </button>
            {isAssignedToAgent && (
              <button
                className="action_button"
                data-testid="actionButton"
                onClick={handleUnassignAgent}
              >
                {t.unassign_agent}
              </button>
            )}
            <div className="separator" />
            {isRoutingEnabled && (
              <button
                className="action_button"
                data-testid="actionButton"
                onClick={handleAddToRouting}
              >
                {t.add_to_routing}
              </button>
            )}
            {isEditOrderEnabled && firstShipperOrderId && (
              <button className="action_button" onClick={handleEditOrder}>
                {t.edit_order}
              </button>
            )}
            {!isUpgrade && allowAssignDriver && (
              <button className="action_button" data-testid="actionButton" onClick={handleAssign}>
                {t.assign}
              </button>
            )}
            <div className="separator" />
            <button
              className="action_button"
              data-testid="actionButton"
              onClick={handleLaunchToThePool}
            >
              {t.launch_to_the_pool}
            </button>
            <button className="action_button" data-testid="actionButton" onClick={handleBreak}>
              {t.break}
            </button>
            <div className="separator" />
            {isAutomaticEnabled && (
              <button
                className="action_button"
                data-testid="actionButton"
                onClick={handleMoveToAuto}
              >
                {t.move_to_auto}
              </button>
            )}
          </>
        )}
        {isLocked && isAutomaticEnabled && (
          <button className="action_button" data-testid="actionButton" onClick={handleMoveToManual}>
            {t.move_to_manual}
          </button>
        )}
        {notLocked && isZendeskEnabled && (
          <>
            <div className="separator" />
            <button
              className="action_button"
              data-testid="actionButton"
              onClick={handleShipperWarningEmail}
            >
              {t.send_warning_email}
            </button>
          </>
        )}
      </div>
      {showAssignAgentModal && (
        <AssignAgent
          onSelected={({ id: agentId }) => assignAgent(agentId)}
          onClosed={onAssignAgentModalClosed}
        />
      )}
    </>
  );
};

const showStyles = {
  display: 'block',
};

const ActionsTooltip = React.memo(
  ({
    className,
    id,
    journeyId,
    shipperOrderIds,
    mutateListFn,
    route,
    pickupDate,
    children,
    isLocked,
    isUpgrade = false,
    isAssignedToAgent = false,
    intl,
  }) => {
    const [shown, setShown] = useState(false);

    const tipRef = useRef(null);

    const handleShow = useCallback(() => {
      setShown(true);
    }, []);

    const handleHide = useCallback(() => {
      setShown(false);
    }, []);

    const onCreate = useCallback((ref) => {
      tipRef.current = ref;
    }, []);

    const onFinish = useCallback(() => {
      if (!tipRef?.current) {
        return;
      }
      tipRef.current.hide();
    }, []);

    return (
      <Tippy
        placement="left"
        interactive={true}
        trigger="click"
        interactiveBorder={5}
        interactiveDebounce={50}
        appendTo={document.body}
        content={
          <TooltipContent
            id={id}
            journeyId={journeyId}
            shipperOrderIds={shipperOrderIds}
            pickupDate={pickupDate}
            route={route}
            isLocked={isLocked}
            isUpgrade={isUpgrade}
            isAssignedToAgent={isAssignedToAgent}
            onFinish={onFinish}
            mutateListFn={mutateListFn}
            intl={intl}
          />
        }
        maxWidth={600}
        theme="light"
        className={className}
        onShow={handleShow}
        onHide={handleHide}
        onCreate={onCreate}
      >
        {React.cloneElement(children, {
          style: {
            ...children?.props?.style,
            ...(shown ? showStyles : {}),
          },
        })}
      </Tippy>
    );
  }
);

export default injectIntl(ActionsTooltip);
